import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { Company, CompanyStatus } from '@/features/multi-company/types/company.types';
import { companyService } from '@/features/multi-company/services/companyService';
import { mockCompanyService } from '@/data/mockCompanies';
import { getStoredCompany, setStoredCompany, companyStorage } from '@/features/multi-company/utils/company-storage';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { updateCompanyId, updateOrganisationId } from '@/lib/auth-fetch';
import { getCurrentUserCompanyAccess } from '@/services/integrationsRedesignService';

interface CompanyContextType {
  currentCompany: Company | null;
  availableCompanies: Company[];
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  refreshCompanyList: () => Promise<void>;
  companySwitchSignal: AbortSignal | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
  employeeId?: string; // Optional - defaults to 'current-user'
}

export function CompanyProvider({ children, employeeId = 'current-user' }: CompanyProviderProps) {
  const { isDemo } = useScreenDataMode('integrations');
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // AbortController to cancel in-flight requests when switching companies (Fix 1)
  const abortControllerRef = useRef<AbortController | null>(null);
  const [companySwitchSignal, setCompanySwitchSignal] = useState<AbortSignal | null>(null);

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let activeCompanies: Company[];
      let userDefaultCompanyId: string | null = null;

      if (isDemo) {
        const mockCompanies = await mockCompanyService.getCompanies();
        activeCompanies = mockCompanies
          .filter(c => c.status === 'active')
          .map((c) => ({
            id: c.id,
            name: c.name,
            code: c.code,
            status: c.status as CompanyStatus,
            description: '',
            productInstances: [],
            financialIntegration: null,
            hrIntegration: null,
            activeProjects: c.activeProjects,
            employeeCount: c.employeeCount,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastSync: null,
          }));
      } else {
        activeCompanies = await companyService.getActiveCompanies();

        // Filter companies by the current user's access list (INT-I-05 / INT-12)
        try {
          const myAccess = await getCurrentUserCompanyAccess();
          const accessibleIds = new Set(
            myAccess.filter((a) => a.hasAccess).map((a) => a.companyId)
          );

          // Always filter — if accessibleIds is empty, the user has no access
          activeCompanies = activeCompanies.filter((c) => accessibleIds.has(c.id));

          // Determine the user's default company from the access list
          const defaultAccess = myAccess.find((a) => a.isDefault);
          if (defaultAccess) {
            const defaultCompany = activeCompanies.find((c) => c.id === defaultAccess.companyId);
            if (defaultCompany) {
              userDefaultCompanyId = defaultCompany.id;
              // Place the default company first so it's selected if nothing is stored
              activeCompanies = [
                defaultCompany,
                ...activeCompanies.filter((c) => c.id !== defaultCompany.id),
              ];
            }
          }
        } catch (accessErr) {
          console.warn('Could not fetch user company access:', accessErr);
          activeCompanies = [];
        }
      }

      setAvailableCompanies(activeCompanies);

      let companyToLoad: Company | null = null;

      // If the user has an explicit default company, always use it on load
      // so the "Set as default" preference is respected on login/reopen.
      // Only fall back to localStorage when no default is configured.
      if (userDefaultCompanyId) {
        companyToLoad = activeCompanies.find((c) => c.id === userDefaultCompanyId) || null;
      }

      if (!companyToLoad) {
        const storedCompanyId = getStoredCompany();
        if (storedCompanyId) {
          companyToLoad = activeCompanies.find((c) => c.id === storedCompanyId) || null;
        }
      }

      if (!companyToLoad && activeCompanies.length > 0) {
        companyToLoad = activeCompanies[0];
      }

      if (companyToLoad) {
        setCurrentCompany(companyToLoad);
        setStoredCompany(companyToLoad.id);
        updateCompanyId(companyToLoad.id);
        const currentOrgId = sessionStorage.getItem('evo_organisation_id') || undefined;
        updateOrganisationId(currentOrgId);
      } else {
        setError('No companies available');
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [isDemo]);

  // Load companies on mount and when mode changes
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  async function switchCompany(companyId: string) {
    try {
      setIsLoading(true);
      setError(null);

      const company = availableCompanies.find((c) => c.id === companyId);
      if (!company) {
        throw new Error('Company not found or access denied');
      }

      // Abort any in-flight requests from the previous company switch (Fix 1)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setCompanySwitchSignal(controller.signal);

      // Clear cache for the old company before switching to ensure fresh data
      if (currentCompany) {
        companyStorage.clearCompanyCache(currentCompany.id);
      }

      setCurrentCompany(company);
      setStoredCompany(companyId);
      updateCompanyId(companyId);
      const currentOrgId = sessionStorage.getItem('evo_organisation_id') || undefined;
      updateOrganisationId(currentOrgId);

      // Yield to the microtask queue so that the global authConfig is fully
      // consistent before any company-changed listeners fire (Fix 3)
      await new Promise(resolve => setTimeout(resolve, 0));

      // Dispatch company-changed event with the abort signal so listeners
      // can cancel their requests if another switch happens (Fix 1 + Fix 5)
      window.dispatchEvent(new CustomEvent('company-changed', {
        detail: { companyId, signal: controller.signal },
      }));
    } catch (err) {
      console.error('Failed to switch company:', err);
      setError('Failed to switch company');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshCompanies() {
    await loadCompanies();
  }

  /**
   * Silently re-fetch the company list without resetting loading state.
   * Designed to be called when the company selector popover is opened so
   * that newly-granted (or revoked) company access is reflected immediately.
   */
  async function refreshCompanyList() {
    try {
      let activeCompanies: Company[];

      if (isDemo) {
        const mockCompanies = await mockCompanyService.getCompanies();
        activeCompanies = mockCompanies
          .filter(c => c.status === 'active')
          .map((c) => ({
            id: c.id,
            name: c.name,
            code: c.code,
            status: c.status as CompanyStatus,
            description: '',
            productInstances: [],
            financialIntegration: null,
            hrIntegration: null,
            activeProjects: c.activeProjects,
            employeeCount: c.employeeCount,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastSync: null,
          }));
      } else {
        activeCompanies = await companyService.getActiveCompanies();

        try {
          const myAccess = await getCurrentUserCompanyAccess();
          const accessibleIds = new Set(
            myAccess.filter((a) => a.hasAccess).map((a) => a.companyId)
          );

          activeCompanies = activeCompanies.filter((c) => accessibleIds.has(c.id));

          const defaultAccess = myAccess.find((a) => a.isDefault);
          if (defaultAccess) {
            const defaultCompany = activeCompanies.find((c) => c.id === defaultAccess.companyId);
            if (defaultCompany) {
              activeCompanies = [
                defaultCompany,
                ...activeCompanies.filter((c) => c.id !== defaultCompany.id),
              ];
            }
          }
        } catch (accessErr) {
          console.warn('Could not fetch user company access:', accessErr);
          activeCompanies = [];
        }
      }

      setAvailableCompanies(activeCompanies);

      // If the current company was revoked, fall back to the first available
      if (currentCompany && !activeCompanies.some((c) => c.id === currentCompany.id)) {
        const fallback = activeCompanies[0] || null;
        setCurrentCompany(fallback);
        if (fallback) {
          // Abort any in-flight requests and create a new controller for the fallback switch
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          const controller = new AbortController();
          abortControllerRef.current = controller;
          setCompanySwitchSignal(controller.signal);

          setStoredCompany(fallback.id);
          updateCompanyId(fallback.id);
          const currentOrgId = sessionStorage.getItem('evo_organisation_id') || undefined;
          updateOrganisationId(currentOrgId);
          window.dispatchEvent(new CustomEvent('company-changed', {
            detail: { companyId: fallback.id, signal: controller.signal },
          }));
        }
      }
    } catch (err) {
      // Silently keep the existing list on failure
      console.warn('Silent company list refresh failed:', err);
    }
  }

  const value: CompanyContextType = {
    currentCompany,
    availableCompanies,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
    refreshCompanyList,
    companySwitchSignal,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
